import { memo } from 'react';
import { useIntl } from 'react-intl';

import type { LearningResourcesTags } from '@didaskill/domain/learning-resources/types';
import { type TAG_VARIANTS, Tag, type TagProps, useTagVariant } from '@didaskill/ui/design-system';
import { learningResourceTagMessages } from '@didaskill/ui/intl';

export type LearningResourceTagProps = Omit<TagProps, 'label' | 'variant' | 'light'> & {
  label: string;
};

/**
 * Returns whether the given label is a known learning-resource tag key.
 * @param label Tag label from a learning resource.
 * @returns True when a localized message exists for the label.
 */
function isLearningResourceTag(label: string): label is LearningResourcesTags {
  return label in learningResourceTagMessages;
}

/**
 * Displays a learning-resource tag with a localized label when the value is known.
 * Falls back to the raw label for legacy or unexpected tag values.
 * @param props Tag props with a string label (enum key or free-form).
 * @returns A light Tag with a deterministic variant.
 */
export const LearningResourceTagBase = ({
  label,
  ...props
}: LearningResourceTagProps): JSX.Element => {
  const intl = useIntl();
  const variant = useTagVariant(label, {
    exclude: [Tag.VARIANTS.PRIMARY],
  }) as TAG_VARIANTS;

  const displayLabel = isLearningResourceTag(label)
    ? intl.formatMessage(learningResourceTagMessages[label])
    : label;

  return <Tag {...props} label={displayLabel} variant={variant} light />;
};

LearningResourceTagBase.displayName = 'LearningResourceTag';

export const LearningResourceTag = memo(LearningResourceTagBase);
